import React, { useEffect, useMemo, useState, useRef } from "react";
import { Col, Container, Row, Button } from "react-bootstrap";
import { useNavigate, useRevalidator } from "react-router-dom";
import { ColDef, GridReadyEvent } from "ag-grid-community";
import { DataGrid } from "../../Controls/DataGrid";
import { PageBar } from "../../Controls/PageBar";
import { createApiService } from "../../shared/apiConfig";
import { useParams } from "react-router-dom";
import { Select } from "../../Controls/Select";
import AdminOperatorIntegrationEnrollModal from "./AdminOperatoroIntegrationEnrollModal";
import { IntegrationPartyApi, ServerTransferEnrolledIntegrationParty, ServerTransferIntegrationParty } from "../../shared/api/axios-client";

export interface IntegrationFacilityParty {
    id: number;
    facilityId: number;
    facilityName: string;
    externalId: string;
    token: string;
    apiSecret: string;
}

const integrationPartyApi = createApiService(IntegrationPartyApi)
export function AdminOperatorIntegrationListPage() {
    const nav = useNavigate();
    const { revalidate } = useRevalidator();
    const [integrations, setIntegrations] = useState<ServerTransferIntegrationParty[]>([]);
    const [facilities, setFacilities] = useState<ServerTransferEnrolledIntegrationParty[]>([]);
    const { operatorId } = useParams<{ operatorId?: string }>();
    const [showEditModal, setShowEditModal] = useState(false);
    const [editInitialData, setEditInitialData] = useState<ServerTransferEnrolledIntegrationParty | undefined>(undefined);
    const [integrationId, setIntegrationId] = useState('');
    const integrationIdRef = useRef(integrationId);
    const [integrationType, setIntegrationType] = useState('');

    const mainColumns: ColDef<ServerTransferEnrolledIntegrationParty>[] = [
        {
            headerName: "",
            width: 50,
            cellRenderer: ({ data }: { data: ServerTransferEnrolledIntegrationParty }) => (
                <input type="checkbox" readOnly checked={!!data.isActive} className="btn border-primary border-1 status-btn text-black d-flex-align-center px-2 mt-3  btn btn-sm" onClick={(e) => enrollIntegration(data)} />
            )
        },
        { field: "facilityName", headerName: "Name" },
        
    ]
    const thirdPartyColumns: ColDef<ServerTransferEnrolledIntegrationParty>[] = [
        { field: "externalId", headerName: "External ID" },
        { field: "token", headerName: "Token" },
        {
            field: "apiSecret", headerName: "API Secret",
            cellRenderer: ({ data }: { data: ServerTransferEnrolledIntegrationParty }) => (
                <div className="w-100" data-secret={data.apiSecret} onMouseDown={showSecretValueOnClick} onBlur={hideSecretValueOnRelease} onMouseUp={hideSecretValueOnRelease}>***</div>
            )
        },
        {
            headerName: "",
            width: 80,
            cellRenderer: ({ data }: { data: ServerTransferEnrolledIntegrationParty }) => (
                data.isActive ? (
                    <button type="button" className="btn border-primary border-1 status-btn text-black d-flex-align-center px-2 mt-1  btn btn-sm" onClick={() => editIntegration(data)}>Edit</button>
                ) : null
            )
        },]
    const columns = useMemo<ColDef<ServerTransferEnrolledIntegrationParty>[]>(() =>
        integrationType === 'Feature' ? mainColumns
            : mainColumns.concat(thirdPartyColumns), [integrationType]);

    const onGridReady = (params: GridReadyEvent) => {
        params.api.sizeColumnsToFit();
    };


    // Place this function definition within the component
    const editIntegration = (data: ServerTransferEnrolledIntegrationParty) => {
        setEditInitialData(data);
        setShowEditModal(true);
    };
    // Place this function definition within the component
    const enrollIntegration = (integration: ServerTransferEnrolledIntegrationParty) => {
        const currentIntegrationId = integrationIdRef.current;
        integrationPartyApi.enrollIntegrationParty({
            id: parseInt(currentIntegrationId),
            serverRequestsEnrollIntegrationPartyRequest: {
                facilityId: integration.facilityId!,
                externalId: '',
                token: '',
                apiSecret: '',
                isActive: !integration.isActive
            }
        }).then(async () => {
            revalidate();
            setShowEditModal(false);
            await loadFacilities(parseInt(currentIntegrationId));
        })
    };
    const showSecretValueOnClick = (e: React.MouseEvent<HTMLSpanElement>) => {
        const secretValue = e.currentTarget.getAttribute('data-secret');
        if (secretValue) {
            e.currentTarget.innerText = secretValue;
        }
    };

    const hideSecretValueOnRelease = (e: any ) => {
        e.currentTarget.innerText = '***';
    };

    const loadFacilities = async (integrationId: number) => {
        const response = await integrationPartyApi.listEnrolledIntegrationParty({ integrationPartyId: integrationId, operatorId: parseInt(operatorId!) });
        if (response.data) {
            setFacilities(response.data.items ?? []);
        } else {
            setFacilities([]);
        }
    };
    const loadIntegrations = async () => {
        const response = await integrationPartyApi.listIntegrationParty({});
        if (response.data) {
            setIntegrations(response.data.items!);
        }
    };
    useEffect(() => {
        loadIntegrations();
    }, [])

    useEffect(() => {
        integrationIdRef.current = integrationId;
    }, [integrationId]);

    const onIntegrationChange = async (event: React.ChangeEvent<HTMLSelectElement>) => {
        const integrationId = event.target.value;
        const selectedOption = event.target.selectedOptions[0];
        const integrationType = selectedOption ? selectedOption.getAttribute('data-type') : '';
        if (integrationId) {
            console.log('channgingIntegrationId', integrationId);
            setIntegrationId(integrationId);
            integrationIdRef.current = integrationId;
            setIntegrationType(integrationType ?? '');
            // You can use integrationType as needed here
            await loadFacilities(parseInt(integrationId));
        }
    };

    const onModalSave = async (integration: ServerTransferEnrolledIntegrationParty) => {
        integrationPartyApi.enrollIntegrationParty({
            id: parseInt(integrationId),
            serverRequestsEnrollIntegrationPartyRequest: {
                facilityId: integration.facilityId!,
                externalId: integration.externalId,
                token: integration.token,
                apiSecret: integration.apiSecret,
                isActive: integration.isActive === undefined
                    ? false
                    : typeof integration.isActive === "boolean"
                        ? integration.isActive
                        : integration.isActive === "true"
            }
        }).then(async () => {
            revalidate();
            setShowEditModal(false);
            await loadFacilities(parseInt(integrationId));
        })
    };
    const onModalClose = (show: boolean) => {
        setShowEditModal(show);
    };


    return <Container fluid className="h-100">
        <AdminOperatorIntegrationEnrollModal show={showEditModal} onSave={onModalSave} setShow={onModalClose} initialData={editInitialData!}></AdminOperatorIntegrationEnrollModal>
        <Row className="h-100">
            <Col className="h-100" style={{ display: "grid", gridTemplateColumns: "1fr", gridTemplateRows: "max-content 1fr 5px" }}>
           
                <PageBar title={
                    <div className="d-flex align-items-center gap-3">
                        <Button variant="link" className="p-0" onClick={() => nav(-1)}> ← Back</Button>
                        <span>Features</span>
                    </div>
                }>
                    <Select label="" onChange={onIntegrationChange}>
                        <option value="">Select Feature</option>
                        {integrations.map((integration) => (
                            <option key={integration.id} value={integration.id} data-type={integration.origin}>
                                {integration.name}
                            </option>
                        ))}
                    </Select>
                </PageBar>
                <div style={{ height: "100%", width: "100%" }}>
                    <DataGrid
                        rowData={facilities}
                        columnDefs={columns}
                        onGridReady={onGridReady}
                    />
                </div>
            </Col>
        </Row>
    </Container>;
}